/**
 * Landing Page Custom Hooks
 *
 * Custom React hooks for landing page animations and interactions.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Smooth scroll for anchor links
 */
export function useSmoothScroll() {
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = (e.target as HTMLElement).closest('a[href^="#"]');
      if (!target) return;

      e.preventDefault();
      const href = target.getAttribute('href');
      if (!href) return;

      const targetElement = document.querySelector(href);
      if (targetElement) {
        targetElement.scrollIntoView({ behavior: 'smooth' });
      }
    };

    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);
}

/**
 * Tracks a `<video>` load state and retries the source once before reporting
 * a hard failure. Mitigates inconsistent range/partial-content loads where a
 * browser holds a stale cached entry and the initial load never fires
 * `loadeddata` (which the previous silent `onError` fallback could not catch,
 * since some failures never emit an error event).
 *
 * Returns:
 *   - failed   true once both the first attempt and the retry have failed
 *   - retryKey bump this on the `<video key={...}>` to force a reload
 *   - handleError  attach to the `<video onError={handleError}>`
 */
export function useVideoLoad(retryDelayMs = 800) {
  const [failed, setFailed] = useState(false);
  const [retryKey, setRetryKey] = useState(0);
  const retriedRef = useRef(false);
  const loadedRef = useRef(false);
  const timerRef = useRef<number | null>(null);

  const handleLoadedData = useCallback(() => {
    loadedRef.current = true;
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const handleError = useCallback(() => {
    if (retriedRef.current) {
      setFailed(true);
      return;
    }
    retriedRef.current = true;
    // Force a fresh fetch of the source (bypasses the bad cached entry).
    setRetryKey((k) => k + 1);
  }, []);

  useEffect(() => {
    // If the video never fires `loadeddata` within the window, assume the
    // load stalled (common with partial-content cache corruption) and retry.
    timerRef.current = window.setTimeout(() => {
      if (!loadedRef.current && !retriedRef.current) {
        retriedRef.current = true;
        setRetryKey((k) => k + 1);
      }
    }, retryDelayMs);

    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
    // retryKey intentionally drives the effect so the timeout resets per retry.
  }, [retryKey, retryDelayMs]);

  return { failed, retryKey, handleLoadedData, handleError };
}



/**
 * Intersection Observer for scroll animations
 */
export function useScrollAnimation<T extends HTMLElement>(
  threshold = 0.1,
  rootMargin = '0px'
) {
  const ref = useRef<T>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.unobserve(element);
          }
        });
      },
      { threshold, rootMargin }
    );

    observer.observe(element);

    return () => {
      observer.unobserve(element);
    };
  }, [threshold, rootMargin]);

  return { ref, isVisible };
}

/**
 * Usage bar animation for pricing/usage section
 */
export function useUsageBarAnimation(targetWidth: number) {
  const ref = useRef<HTMLDivElement>(null);
  const [isAnimated, setIsAnimated] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !isAnimated) {
            setTimeout(() => {
              element.style.width = `${targetWidth}%`;
            }, 200);
            setIsAnimated(true);
            observer.unobserve(element);
          }
        });
      },
      { threshold: 0.5 }
    );

    observer.observe(element);

    return () => {
      observer.unobserve(element);
    };
  }, [targetWidth, isAnimated]);

  return { ref, isAnimated };
}

/**
 * Typing effect for terminal-style display
 * Alternative version that works with a single string array
 */
export function useTerminalEffect(lines: string[], enabled = true, delay = 30) {
  const [displayText, setDisplayText] = useState('');
  const elementRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!enabled || !elementRef.current || lines.length === 0) return;

    let currentText = '';
    let lineIdx = 0;
    let charIdx = 0;
    let timer: NodeJS.Timeout;

    const typeChar = () => {
      if (lineIdx >= lines.length) return;

      const currentLine = lines[lineIdx];

      if (charIdx < currentLine.length) {
        currentText += currentLine[charIdx];
        setDisplayText(currentText);
        charIdx++;
        timer = setTimeout(typeChar, delay);
      } else {
        lineIdx++;
        charIdx = 0;
        if (lineIdx < lines.length) {
          currentText += '\n';
          setDisplayText(currentText);
          timer = setTimeout(typeChar, delay * 15);
        }
      }
    };

    // Small initial delay before starting
    timer = setTimeout(typeChar, 500);

    return () => clearTimeout(timer);
  }, [lines, enabled, delay]);

  return { displayText, elementRef };
}
